
// export default VivaPerformanceCard;
import React, { useEffect, useState } from "react";

import {
    Mic,
    Eye,
    Brain,
    Clock3,
    FileText,
    Award,
    CheckCircle2,
    XCircle,
    Sparkles,
    TrendingUp
} from "lucide-react";

import { useTheme } from "../css/ThemeContext";
import "./VivaPerformanceCard.css";
import axios from "axios";

const VivaPerformanceCard = () => {

    const { theme, generateGradient } = useTheme();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedExamId, setSelectedExamId] = useState("");
    const [dropdownExams, setDropdownExams] = useState([]);

    const [performance, setPerformance] = useState(null);
    const [topImprovements, setTopImprovements] = useState([]);
    const [strength, setStrength] = useState("");
    const [weakness, setWeakness] = useState("");
    const [hasData, setHasData] = useState(true); // Add this state
    const [message, setMessage] = useState(""); // Add this state

    // =========================================================
    // THEME
    // =========================================================

    const headerGradient = generateGradient(
        theme.appGradient.start,
        theme.appGradient.end
    );

    // =========================================================
    // FORMAT TIME
    // =========================================================

    // const formatTime = (seconds) => {

    //     if (!seconds || seconds === 0) {
    //         return "0m 0s";
    //     }

    //     const mins = Math.floor(seconds / 60);
    //     const secs = seconds % 60;

    //     return `${mins}m ${secs}s`;
    // };

    const formatTime = (seconds, showTotalSeconds = false) => {
        console.log("Formatting seconds:", seconds);

        if (!seconds || seconds === 0) {
            return "0 mins, 0 secs";
        }

        if (showTotalSeconds) {
            return `0 mins, ${seconds} secs`;
        }

        // Convert total backend seconds back into constituent components
        const mins = Math.floor(seconds / 60);
        const secs = Math.round(seconds % 60);

        // Grammar filters for standalone metrics
        const minLabel = mins === 1 ? "minute" : "minutes";
        const secLabel = secs === 1 ? "second" : "seconds";

        return `${mins} ${minLabel}, ${secs} ${secLabel}`;
    };


    // =========================================================
    // FETCH ANALYTICS
    // =========================================================

    const fetchAnalytics = async (examId = null) => {

        try {

            setLoading(true);
            setError(null);
            setHasData(true);

            let url = "/dashboard/viva-analytics/";

            if (examId) {
                url += `?exam_employee_id=${examId}`;
            }

            const response = await axios.get(url, {
                withCredentials: true,
                headers: {
                    "Content-Type": "application/json",
                },
            });

            const data = response.data;

            // Check if we have data or if it's an empty state
            if (data.hasData === false) {
                setHasData(false);
                setMessage(data.message || "No data available");
                setPerformance(null);
                setDropdownExams(data.dropdown_exams || []);
                setTopImprovements([]);
                setStrength("");
                setWeakness("");
                return;
            }

            setHasData(true);
            setMessage("");

            setPerformance(data.selectedExam);

            setDropdownExams(data.dropdown_exams || []);

            setTopImprovements(data.top_improvements || []);

            setStrength(data.strength || "");

            setWeakness(data.weakness || "");

            if (!examId && data.selectedExam?.exam_employee_id) {

                setSelectedExamId(
                    data.selectedExam.exam_employee_id
                );

            } else if (examId) {

                setSelectedExamId(examId);
            }

        } catch (err) {

            console.error(err);

            setError(err.message);

        } finally {

            setLoading(false);
        }
    };

    // =========================================================
    // INITIAL LOAD
    // =========================================================

    useEffect(() => {
        fetchAnalytics();
    }, []);

    // =========================================================
    // DROPDOWN CHANGE
    // =========================================================

    const handleExamChange = (e) => {

        const value = e.target.value;

        setSelectedExamId(value);

        fetchAnalytics(value);
    };

    // =========================================================
    // LOADING
    // =========================================================

    if (loading) {

        return (
            <div className="viva-loading">
                Loading Competency Review analytics...
            </div>
        );
    }

    // =========================================================
    // ERROR
    // =========================================================

    if (error) {

        return (
            <div className="viva-error">
                Error: {error}
            </div>
        );
    }

    // =========================================================
    // EMPTY
    // =========================================================

    if (!hasData || !performance) {
        return (
            <div className="viva-empty-state">
                <div className="empty-state-content">
                    <div className="empty-icon">📊</div>
                    <h3>No Competency Review Data Available</h3>
                    <p>{message || "Complete a competency review to see your performance analytics here."}</p>
                </div>
            </div>
        );
    }

    // =========================================================
    // SCORE CIRCLE
    // =========================================================

    const circumference = 2 * Math.PI * 42;

    // =========================================================
    // ICONS
    // =========================================================

    const getTopicIcon = (topic) => {

        const value = topic?.toLowerCase();

        if (value?.includes("confidence")) {
            return <Mic size={20} />;
        }

        if (
            value?.includes("clarity") ||
            value?.includes("communication")
        ) {
            return <Brain size={20} />;
        }

        return <Eye size={20} />;
    };

    return (

        <div className="viva-performance-wrapper">

            {/* ================================================= */}
            {/* HEADER */}
            {/* ================================================= */}

            <div
                className="viva-header"
                style={{
                    background: headerGradient
                }}
            >

                {/* LEFT */}

                <div className="viva-header-left">

                    <div className="viva-mini-title">
                        {performance.exam_title}
                    </div>

                    <h2 className="viva-main-title">
                        Your Competency Review Performance 🚀
                    </h2>

                    <p className="viva-description">
                        {performance.user_name} · {performance.performance_message}
                    </p>

                    {/* PILLS */}

                    <div className="pill-row">

                        <span className="pill">
                            <FileText size={15} />
                            {performance.questions_attempted}/
                            {performance.question_count} Attempted
                        </span>

                        <span className="pill">
                            <Clock3 size={15} />
                            {formatTime(performance.time_taken_seconds)}
                        </span>

                        <span className="pill grade">
                            <Award size={15} />
                            Grade {performance.grade}
                        </span>

                    </div>
                </div>

                {/* RIGHT */}

                <div className="viva-header-right">

                    {/* DROPDOWN */}

                    <select
                        value={selectedExamId}
                        onChange={handleExamChange}
                        className="exam-dropdown"
                    >

                        {dropdownExams.map((exam) => (

                            <option
                                key={exam.value}
                                value={exam.value}
                            >
                                {exam.label}
                            </option>
                        ))}
                    </select>

                    {/* SCORE */}

                    <div className="score-result-container">

                        {/* CIRCLE */}

                        <div className="score-circle">

                            <svg
                                viewBox="0 0 100 100"
                                className="circle-svg"
                            >

                                <circle
                                    cx="50"
                                    cy="50"
                                    r="42"
                                    className="circle-bg"
                                />

                                <circle
                                    cx="50"
                                    cy="50"
                                    r="42"
                                    className="circle-progress"
                                    strokeDasharray={circumference}
                                    strokeDashoffset={
                                        circumference *
                                        (
                                            1 -
                                            performance.overall_score / 100
                                        )
                                    }
                                />
                            </svg>

                            <div className="circle-content">

                                <div>
                                    {performance.overall_score}%
                                </div>

                                <div className="circle-label">
                                    OVERALL
                                </div>
                            </div>
                        </div>

                        {/* RESULT */}

                        <div className="result-block">

                            <div>
                                Result:
                            </div>
                            <div
                                className={`result-status ${performance.is_passed
                                    ? "result-pass"
                                    : "result-fail"
                                    }`}
                            >

                                {/* {
                                    performance.is_passed
                                        ? <CheckCircle2 size={18} />
                                        : <XCircle size={18} />
                                } */}

                                {
                                    performance.is_passed
                                        ? "Passed"
                                        : "Not Passed"
                                }
                            </div>

                            <div className="result-meta">
                                Threshold : {performance.pass_percent}% required
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ================================================= */}
            {/* IMPROVEMENTS */}
            {/* ================================================= */}

            <div className="improve-section">

                <h3>Areas to improve</h3>

                <p className="subtext">
                    Focus on these areas to improve your next competency review performance
                </p>

                <div className="improve-grid">

                    {topImprovements.map((tip, index) => {

                        const colors = [
                            {
                                border: "#6366f1",
                                bg: "#eef2ff",
                            },
                            {
                                border: "#22c55e",
                                bg: "#ecfdf5",
                            },
                            {
                                border: "#f97316",
                                bg: "#fff7ed",
                            },
                        ];

                        const style = colors[index % 3];

                        return (

                            <div
                                key={tip.id}
                                className="improve-card"
                                style={{
                                    borderLeft: `4px solid ${style.border}`,
                                }}
                            >

                                <div className="improve-top">

                                    <div
                                        className="icon-box"
                                        style={{
                                            background: style.bg,
                                        }}
                                    >
                                        {getTopicIcon(tip.topic)}
                                    </div>

                                    <div className="improve-info">

                                        <div className="improve-title">
                                            {tip.topic}
                                        </div>

                                        <span className="improve-badge">
                                            Score: {tip.score}%
                                        </span>

                                    </div>
                                </div>

                                <p className="improve-desc">
                                    {tip.message}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ================================================= */}
            {/* SUMMARY */}
            {/* ================================================= */}

            <div className="summary-grid">

                {/* STRENGTH */}

                <div className="summary-card strength-card">

                    <div className="summary-icon strength-icon">
                        <Sparkles size={18} />
                    </div>

                    <div>

                        <div className="summary-title">
                            What you did well
                        </div>

                        <div className="summary-text strength-text">
                            {strength}
                        </div>
                    </div>
                </div>

                {/* WEAKNESS */}

                <div className="summary-card weakness-card">

                    <div className="summary-icon weakness-icon">
                        <TrendingUp size={18} />
                    </div>

                    <div>

                        <div className="summary-title">
                            Where to grow
                        </div>

                        <div className="summary-text weakness-text">
                            {weakness}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VivaPerformanceCard;
